import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ClipboardList, Clock, CalendarDays, Pencil, Ban, Wand2, Power, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import { TimePicker } from "@/components/TimePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDateIt, fmtDuration } from "@/lib/services";

const HYBRID_TYPES = ["Ufficio", "Vice coordinamento", "Coordinamento"];
const AUTO_UNIVERSAL_TYPES = ["Allattamento"];
const WEEKDAYS = [[1, "Lun"], [2, "Mar"], [3, "Mer"], [4, "Gio"], [5, "Ven"], [6, "Sab"], [7, "Dom"]];
const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function todayRomeIso() {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  return p;
}

function hhmmToMin(s) {
  if (!s || !s.includes(":")) return null;
  const [h, m] = s.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

const empty = { appalto_id: "", date: todayRomeIso(), indirect_type: "", hours: "" };

export default function IndirectHours() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHybrid = (user?.available_profiles || []).includes("UFFICIO") && (user?.available_profiles || []).includes("LAVORATORE");
  const autoTypes = isHybrid ? [...AUTO_UNIVERSAL_TYPES, ...HYBRID_TYPES] : [...AUTO_UNIVERSAL_TYPES];
  const [appalti, setAppalti] = useState([]);
  const [types, setTypes] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editRec, setEditRec] = useState(null);
  const [cancelRec, setCancelRec] = useState(null);
  const [showAuto, setShowAuto] = useState(false);

  const load = async () => {
    const [a, t, l] = await Promise.all([
      api.get("/my-appalti"), api.get("/indirect-types"), api.get("/indirect-hours"),
    ]);
    setAppalti(a.data || []);
    setTypes(t.data || []);
    setList(l.data || []);
    if ((a.data || []).length === 1) setForm((f) => ({ ...f, appalto_id: a.data[0].id }));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.appalto_id) { setError("Seleziona l'appalto"); return; }
    if (!form.indirect_type) { setError("Seleziona la tipologia"); return; }
    const h = Number(form.hours);
    if (!h || h <= 0) { setError("Indica il numero di ore"); return; }
    if ((h * 60) % 5 !== 0) { setError("Le ore devono essere a intervalli di 5 minuti"); return; }
    setSaving(true);
    try {
      await api.post("/indirect-hours", {
        appalto_id: form.appalto_id, date: form.date, indirect_type: form.indirect_type, hours: h,
      });
      toast.success("Ora indiretta dichiarata");
      setForm((f) => ({ ...empty, appalto_id: f.appalto_id, date: f.date }));
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="worker-theme min-h-screen bg-[#EDF0F4] pb-10">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <button data-testid="indirect-back-btn" onClick={() => navigate("/lavoratore")}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
          <ArrowLeft className="h-4 w-4" /> Home
        </button>

        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shadow-inner"><ClipboardList className="h-5 w-5 text-[#22364D]" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Ore indirette</h1>
            <p className="text-sm text-slate-500 mt-0.5">Ore lavorate non riferite a una prestazione diretta</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
          <div className="space-y-1.5">
            <Label>Appalto</Label>
            <Select value={form.appalto_id} onValueChange={(v) => setForm((f) => ({ ...f, appalto_id: v }))}>
              <SelectTrigger data-testid="indirect-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
              <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <DatePicker label="Data" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} testid="indirect-date" />

          <div className="space-y-1.5">
            <Label>Tipologia</Label>
            <Select value={form.indirect_type} onValueChange={(v) => setForm((f) => ({ ...f, indirect_type: v }))}>
              <SelectTrigger data-testid="indirect-type-select" className="h-12"><SelectValue placeholder="Seleziona la tipologia" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t} data-testid={`indirect-type-${t}`}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Numero di ore</Label>
            <Input type="number" step="0.5" min="0" value={form.hours} data-testid="indirect-hours-input"
              onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} className="h-11" placeholder="Es. 3" />
          </div>

          {error && <p className="text-sm text-destructive" data-testid="indirect-error">{error}</p>}

          <Button type="submit" disabled={saving} data-testid="indirect-submit-btn" className="w-full h-12 rounded-lg font-semibold">
            {saving ? "Invio…" : "Dichiara ore indirette"}
          </Button>
        </form>

        {(isHybrid || form.indirect_type === "Allattamento") && (
          <div className="mt-5 rounded-2xl border border-[#22364D]/25 bg-[#EDF5FA] p-4" data-testid="auto-schedule-card">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#22364D]/15 flex items-center justify-center shrink-0">
                <Wand2 className="h-5 w-5 text-[#22364D]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold text-slate-800">Compilazione automatica</p>
                <p className="text-sm text-slate-600 mt-0.5">
                  {isHybrid
                    ? <>Imposta una regola mensile per <span className="font-medium">Ufficio</span>, <span className="font-medium">Vice coordinamento</span>, <span className="font-medium">Coordinamento</span> o <span className="font-medium">Allattamento</span>: le ore vengono registrate automaticamente giorno per giorno, escludendo le festività.</>
                    : <>Imposta una regola mensile per <span className="font-medium">Allattamento</span>: le ore vengono registrate automaticamente giorno per giorno, escludendo le festività.</>}
                </p>
                <Button type="button" onClick={() => setShowAuto(true)} data-testid="auto-schedule-open-btn"
                  className="mt-3 h-10 rounded-lg font-semibold bg-[#22364D] hover:bg-[#22364D]">
                  <Zap className="h-4 w-4 mr-1.5" /> Configura compilazione automatica
                </Button>
              </div>
            </div>
          </div>
        )}

        <h2 className="orbit-area-title font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Le tue ore indirette</h2>
        <div className="space-y-2.5" data-testid="indirect-list">
          {loading ? (
            [0, 1].map((i) => <div key={i} className="rounded-xl bg-white border border-slate-200 h-16 animate-pulse" />)
          ) : list.length === 0 ? (
            <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="indirect-empty">Non hai ancora dichiarato ore indirette.</CardContent></Card>
          ) : (
            list.map((r, i) => {
              const cancelled = r.status === "cancelled";
              return (
              <motion.div key={r.id} data-testid={`indirect-card-${i}`}
                initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: Math.min(i * 0.035, 0.3) }}
                className={`rounded-xl border shadow-sm p-4 flex flex-col gap-3 ${cancelled ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"}`}>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#EDF5FA] border border-[#C4D8E6] flex items-center justify-center shrink-0"><ClipboardList className="h-4 w-4 text-[#22364D]" /></div>
                  <div className="min-w-0 flex-1">
                    <p className={`font-semibold leading-tight flex items-center gap-2 flex-wrap ${cancelled ? "text-slate-500 line-through" : "text-slate-800"}`}>
                      {r.indirect_type}
                      {r.auto && !cancelled && <span data-testid={`indirect-auto-badge-${i}`} className="no-underline text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#22364D]/15 text-[#22364D]">Auto</span>}
                      {cancelled && <span data-testid={`indirect-cancelled-badge-${i}`} className="no-underline text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">Annullata</span>}
                    </p>
                    <p className="text-xs text-slate-500 leading-tight mt-1 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {fmtDateIt(r.date)}</span>
                      {r.appalto_nome && <span>· {r.appalto_nome}</span>}
                    </p>
                  </div>
                  <span className={`text-sm font-bold inline-flex items-center gap-1 shrink-0 ${cancelled ? "text-slate-400" : "text-[#22364D]"}`}><Clock className="h-4 w-4" /> {fmtDuration(r.duration_minutes)}</span>
                </div>
                {!cancelled && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" data-testid={`indirect-edit-${i}`}
                      onClick={() => setEditRec(r)} className="rounded-lg h-9 flex-1 text-xs">
                      <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifica
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`indirect-cancel-${i}`}
                      onClick={() => setCancelRec(r)} className="rounded-lg h-9 flex-1 text-xs text-destructive border-destructive/30 hover:bg-destructive/5">
                      <Ban className="h-3.5 w-3.5 mr-1.5" /> Annulla
                    </Button>
                  </div>
                )}
              </motion.div>
              );
            })
          )}
        </div>
      </main>
      {editRec && (
        <IndirectEditDialog record={editRec} appalti={appalti} types={types}
          onClose={() => setEditRec(null)}
          onSaved={() => { setEditRec(null); load(); }} />
      )}
      {cancelRec && (
        <ConfirmCancelDialog
          onClose={() => setCancelRec(null)}
          onConfirm={async () => {
            try { await api.post(`/indirect-hours/${cancelRec.id}/cancel`); toast.success("Ora indiretta annullata"); setCancelRec(null); load(); }
            catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
          }} />
      )}
      {showAuto && (
        <AutoScheduleDialog appalti={appalti} autoTypes={autoTypes}
          onClose={() => setShowAuto(false)}
          onChanged={() => load()} />
      )}
    </div>
  );
}

function AutoScheduleDialog({ appalti, autoTypes, onClose, onChanged }) {
  const blank = { id: null, causale: "", appaltoIds: new Set(appalti.length === 1 ? [appalti[0].id] : []),
    priority: appalti.length === 1 ? appalti[0].id : "", days: new Set([1, 2, 3, 4, 5]), hours: "6", note: "" };
  const [f, setF] = useState(blank);
  const [schedules, setSchedules] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSchedules = async () => {
    try { const { data } = await api.get("/indirect-schedules"); setSchedules(data || []); }
    catch { setSchedules([]); }
  };
  useEffect(() => { loadSchedules(); }, []);

  const toggleDay = (d) => { const n = new Set(f.days); n.has(d) ? n.delete(d) : n.add(d); setF((s) => ({ ...s, days: n })); };
  const preset = (arr) => setF((s) => ({ ...s, days: new Set(arr) }));
  const toggleAppalto = (id) => {
    const n = new Set(f.appaltoIds);
    n.has(id) ? n.delete(id) : n.add(id);
    let pr = f.priority;
    if (n.size === 1) pr = [...n][0];
    else if (!n.has(pr)) pr = "";
    setF((s) => ({ ...s, appaltoIds: n, priority: pr }));
  };
  const resetForm = () => setF({ ...blank, appaltoIds: new Set(appalti.length === 1 ? [appalti[0].id] : []) });
  const editRule = (s) => setF({ id: s.id, causale: s.indirect_type, appaltoIds: new Set(s.appalto_ids || []),
    priority: s.priority_appalto_id || "", days: new Set(s.weekdays || []), hours: String(s.hours_per_day ?? ""), note: s.note || "" });

  const submit = async (e) => {
    e.preventDefault(); setError("");
    const ids = [...f.appaltoIds];
    if (!f.causale) return setError("Seleziona la causale");
    if (ids.length === 0) return setError("Seleziona almeno un appalto");
    if (ids.length > 1 && !f.priority) return setError("Con più appalti scegli l'Appalto prioritario");
    if (f.days.size === 0) return setError("Seleziona almeno un giorno della settimana");
    const h = Number(f.hours);
    if (!h || h <= 0) return setError("Indica le ore giornaliere");
    if ((h * 60) % 5 !== 0) return setError("Le ore devono essere a intervalli di 5 minuti");
    setSaving(true);
    try {
      await api.post("/indirect-schedules", {
        id: f.id, indirect_type: f.causale, appalto_ids: ids,
        priority_appalto_id: ids.length === 1 ? ids[0] : f.priority,
        weekdays: [...f.days].sort((a, b) => a - b), hours_per_day: h, note: f.note.trim() || null,
      });
      toast.success(f.id ? "Regola aggiornata" : "Compilazione automatica attivata");
      resetForm(); await loadSchedules(); onChanged();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const disableSchedule = async (id) => {
    try {
      await api.delete(`/indirect-schedules/${id}`);
      toast.success("Regola disattivata (le ore già registrate restano)");
      if (f.id === id) resetForm();
      await loadSchedules(); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const active = schedules.filter((s) => s.active);
  const multi = f.appaltoIds.size > 1;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="auto-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Compilazione automatica</DialogTitle>
          <DialogDescription>
            Le regole restano attive anche nei mesi successivi finché non le modifichi o disattivi. Le ore diventano registrazioni reali solo quando la giornata arriva; festività, ferie e permessi sono gestiti automaticamente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {f.id && <div className="text-xs font-semibold text-[#22364D]" data-testid="auto-edit-indicator">Modifica regola in corso</div>}
          {error && <div data-testid="auto-schedule-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="space-y-1.5">
            <Label>Causale</Label>
            <Select value={f.causale} onValueChange={(v) => setF((s) => ({ ...s, causale: v }))}>
              <SelectTrigger data-testid="auto-causale-select" className="h-11"><SelectValue placeholder="Seleziona la causale" /></SelectTrigger>
              <SelectContent>{(autoTypes || HYBRID_TYPES).map((t) => <SelectItem key={t} value={t} data-testid={`auto-causale-${t}`}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Appalti</Label>
            <div className="rounded-lg border border-slate-200 divide-y" data-testid="auto-appalti-list">
              {appalti.map((a) => (
                <label key={a.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={f.appaltoIds.has(a.id)} onCheckedChange={() => toggleAppalto(a.id)} data-testid={`auto-appalto-${a.id}`} />
                  <span className="text-sm font-medium text-slate-700">{a.nome}</span>
                </label>
              ))}
            </div>
          </div>
          {multi && (
            <div className="space-y-1.5">
              <Label>Appalto prioritario <span className="text-slate-400 font-normal">(destinazione nel Libro Presenze)</span></Label>
              <Select value={f.priority} onValueChange={(v) => setF((s) => ({ ...s, priority: v }))}>
                <SelectTrigger data-testid="auto-priority-select" className="h-11"><SelectValue placeholder="Scegli l'appalto prioritario" /></SelectTrigger>
                <SelectContent>{[...f.appaltoIds].map((id) => { const a = appalti.find((x) => x.id === id); return <SelectItem key={id} value={id}>{a?.nome || id}</SelectItem>; })}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Giorni della settimana</Label>
              <div className="flex gap-1.5">
                <button type="button" data-testid="auto-preset-lunven" onClick={() => preset([1, 2, 3, 4, 5])} className="text-xs font-medium px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50">Lun–Ven</button>
                <button type="button" data-testid="auto-preset-lunsab" onClick={() => preset([1, 2, 3, 4, 5, 6])} className="text-xs font-medium px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50">Lun–Sab</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map(([n, lbl]) => (
                <button key={n} type="button" data-testid={`auto-day-${n}`} onClick={() => toggleDay(n)}
                  className={`h-9 w-12 rounded-lg text-xs font-semibold border transition-colors ${f.days.has(n) ? "bg-[#22364D] text-white border-[#22364D]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Ore al giorno</Label>
              <Input type="number" step="0.5" min="0" value={f.hours} data-testid="auto-hours-input"
                onChange={(e) => setF((s) => ({ ...s, hours: e.target.value }))} className="h-11" placeholder="Es. 6" />
            </div>
            <div className="space-y-1.5">
              <Label>Nota <span className="text-slate-400 font-normal">(opzionale)</span></Label>
              <Input value={f.note} data-testid="auto-note-input" maxLength={40}
                onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} className="h-11" placeholder="Es. Nido" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {f.id && <Button type="button" variant="outline" onClick={resetForm} disabled={saving} data-testid="auto-new-btn" className="rounded-lg h-11">Nuova regola</Button>}
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="rounded-lg h-11">Chiudi</Button>
            <Button type="submit" disabled={saving} data-testid="auto-schedule-save" className="rounded-lg h-11 font-semibold bg-[#22364D] hover:bg-[#22364D]">
              {saving ? "Salvataggio…" : f.id ? "Salva regola" : "Attiva regola"}
            </Button>
          </DialogFooter>
        </form>

        <div className="mt-2 pt-4 border-t border-slate-200 space-y-2" data-testid="auto-schedule-list">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Regole automatiche attive</p>
          {active.length === 0 ? (
            <p className="text-xs text-slate-400" data-testid="auto-schedule-empty">Nessuna regola attiva. Configurane una qui sopra.</p>
          ) : active.map((s) => (
            <div key={s.id} data-testid={`auto-schedule-row-${s.indirect_type}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{s.indirect_type}{s.note ? ` ${s.note}` : ""} · {s.hours_per_day}h/giorno</p>
                <p className="text-xs text-slate-500">
                  {s.weekdays.map((w) => WEEKDAYS[w - 1][1]).join(", ")}
                  {s.priority_appalto_nome ? ` · ${s.priority_appalto_nome}` : ""}
                  {(s.appalto_ids || []).length > 1 ? ` (+${s.appalto_ids.length - 1})` : ""}
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button type="button" size="sm" variant="outline" data-testid={`auto-schedule-edit-${s.indirect_type}`}
                  onClick={() => editRule(s)} className="rounded-lg h-8 text-xs">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Modifica
                </Button>
                <Button type="button" size="sm" variant="outline" data-testid={`auto-schedule-disable-${s.indirect_type}`}
                  onClick={() => disableSchedule(s.id)} className="rounded-lg h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/5">
                  <Power className="h-3.5 w-3.5 mr-1" /> Disattiva
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmCancelDialog({ onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="indirect-cancel-dialog">
        <DialogHeader><DialogTitle className="font-heading">Annullare questa Ora indiretta?</DialogTitle>
          <DialogDescription>L'ora indiretta resterà nello storico come "Annullata" e non sarà più conteggiata.</DialogDescription></DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} data-testid="indirect-cancel-abort" className="rounded-lg">Annulla</Button>
          <Button data-testid="indirect-cancel-confirm" disabled={busy} onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
            className="rounded-lg bg-destructive hover:bg-destructive/90 text-white">{busy ? "Attendi…" : "Conferma annullamento"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IndirectEditDialog({ record, appalti, types, onClose, onSaved }) {
  const [form, setForm] = useState({
    appalto_id: record.appalto_id, date: record.date, indirect_type: record.indirect_type,
    hours: record.duration_minutes ? String(record.duration_minutes / 60) : "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    const h = Number(form.hours);
    if (!h || h <= 0) { setError("Indica il numero di ore"); return; }
    if ((h * 60) % 5 !== 0) { setError("Le ore devono essere a intervalli di 5 minuti"); return; }
    setSaving(true);
    try {
      await api.patch(`/indirect-hours/${record.id}`, {
        appalto_id: form.appalto_id, date: form.date, indirect_type: form.indirect_type, hours: h });
      toast.success("Ora indiretta aggiornata");
      onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="indirect-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">Modifica ora indiretta</DialogTitle>
          <DialogDescription>Correggi appalto, data, tipologia e numero di ore.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {error && <div data-testid="indirect-edit-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="space-y-1.5">
            <Label>Appalto</Label>
            <Select value={form.appalto_id} onValueChange={(v) => setForm((f) => ({ ...f, appalto_id: v }))}>
              <SelectTrigger data-testid="indirect-edit-appalto" className="h-11"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
              <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DatePicker label="Data" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} testid="indirect-edit-date" />
          <div className="space-y-1.5">
            <Label>Tipologia</Label>
            <Select value={form.indirect_type} onValueChange={(v) => setForm((f) => ({ ...f, indirect_type: v }))}>
              <SelectTrigger data-testid="indirect-edit-type" className="h-11"><SelectValue placeholder="Tipologia" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Numero di ore</Label>
            <Input type="number" step="0.5" min="0" value={form.hours} data-testid="indirect-edit-hours"
              onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} className="h-11" placeholder="Es. 3" />
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="indirect-edit-save" className="w-full rounded-lg h-11 font-semibold">{saving ? "Salvataggio…" : "Salva modifica"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
